// Core Vue imports
import { createApp } from 'vue';
import App from './App';

// UI and styling
import vuetify from './plugins/vuetify';
import '@mdi/font/css/materialdesignicons.css';
import './styles/main.scss';
import VueApexCharts from 'vue3-apexcharts';
import JsonViewer from 'vue-json-viewer';

// Store and Router
import { createPinia } from 'pinia';
import { piniaResetPlugin } from '@/plugins/piniaReset';
import router from '@/router';

// Configuration
import { loadSentry, logAppError } from '@/config/sentryConfig';
import * as Sentry from '@sentry/vue';

// Directives
import trackingDirective from '@/directives/tracking';
import sanitizeDirective from '@/directives/sanitize';

// These modules execute their code immediately when imported
import '@/config/cookieHub';

// Date and time handling
import dayjs from './plugins/dayjs';

// Services
import '@/config/amplifyConfig';
import { getCurrentUser } from 'aws-amplify/auth';
import { logout, LogoutReason } from '@/services/auth/logoutService';
import mixpanel from 'mixpanel-browser';
import QuicksightEmbeddingContext from '@/services/quicksight/QuicksightEmbeddingContext';

// Vue Query
import { VueQueryPlugin } from '@tanstack/vue-query';
import { vueQueryOptions } from '@/config/vueQuery';

// Internationalization
import { useInternationalization } from '@/i18n';

// Composables
import { useAuth } from '@/composables/auth/useAuth';

// Environment
import { isProduction, isStage } from '@/utils/environment';

// Initialize Mixpanel for session recordings only (event tracking moved to backend)
mixpanel.init(import.meta.env.VITE_MIXPANEL_ID, {
  debug: false,
  track_pageview: false,
  persistence: 'localStorage',
  // The number represents the percentage of the total sessions to send to mixpanel
  record_sessions_percent: isProduction ? 20 : 0
});

// Create the Vue app instance
const app = createApp(App);
window.Backoffice = app;

// Set up Quicksight embedding
app.config.globalProperties.$quicksightEmbeddingContext =
  new QuicksightEmbeddingContext();

// Set up internationalization
const { i18n } = useInternationalization();
app.use(i18n);

// Configure day.js with locale settings
const locale = i18n.global.locale.value;
dayjs.locale(locale);
dayjs.updateLocale(locale, { weekStart: 1 }); // Monday as first day of week
app.config.globalProperties.$dayjs = dayjs;

// Register directives
trackingDirective(app);
sanitizeDirective(app);

// Handle chunk loading errors with official Vite solution
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

// Initialize Sentry for Stage and Prod
const isMainRelease = isProduction || isStage;

if (isMainRelease) {
  try {
    await loadSentry(app, router);
  } catch (e) {
    console.warn('Sentry failed to initialize', e);
  }
}

// Register UI components and core plugins
app.use(JsonViewer);
app.use(VueApexCharts);

// Setup Pinia with reset plugin
const pinia = createPinia();
pinia.use(piniaResetPlugin);
app.use(pinia);

// Tan Stack: Vue Query for data fetching and caching
app.use(VueQueryPlugin, vueQueryOptions);

app.use(vuetify);

/**
 * Initialize the application
 * This is the main entry point for the application
 */
const initializeApp = async () => {
  try {
    // Get auth functions from the composable
    const { initApp } = useAuth(app);

    // Try to get the current authenticated user
    let userExists = false;
    try {
      await getCurrentUser();
      userExists = true;
    } catch (userError) {
      console.log('No authenticated user found:', userError.message);
      // This is normal - user is just visiting the login page with no session
    }

    // If user exists, try to initialize the app
    if (userExists) {
      try {
        await initApp();
      } catch (initError) {
        console.log(
          'App initialization failed, forcing logout:',
          initError.message
        );

        logAppError({
          error: initError,
          functionName: 'initializeApp',
          section: 'initApp_failure',
          level: 'warning',
          message: 'App initialization failed for authenticated user'
        });

        // Guard against overwriting SessionExpired logout reason from interceptor
        const isSessionExpiredError =
          initError?.isSessionExpired === true ||
          initError?.response?.status === 401;
        if (!isSessionExpiredError) {
          await logout(app, LogoutReason.InitializationError);
        }
      }
    }
  } catch (error) {
    console.error('Application initialization failed:', error);

    // Send error to Sentry
    Sentry.withScope((scope) => {
      scope.setFingerprint(['app-initialization']);
      scope.setTag('section', 'app_initialization');
      scope.setLevel('fatal'); // Marks it with a red "Fatal" badge in the UI

      Sentry.captureException(error, {
        extra: {
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      });
    });
  } finally {
    // Always register router and mount the app, regardless of auth state
    // This ensures the app is always responsive, even on initialization failure
    app.use(router);
    app.mount('#app');
  }
};

// Start the application initialization
initializeApp();
